import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { EditButton, DeleteButton } from "@refinedev/mui";
import { Chip } from "@mui/material";
import DevicesIcon from '@mui/icons-material/Devices';
import StoreIcon from '@mui/icons-material/Store';

import { TableCell, ActiveDiscountSwitch, FeaturedDiscountSwitch } from "@shared/components";
import { Company } from "@modules/companies/types";

export const useDiscountsListColumns = (
  onRefetch: () => void,
  translate: (key: string) => string,
  companies?: Company[],
): GridColDef[] => {
  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: "id",
        headerName: translate("v1/discount_admin.fields.id"),
        width: 80,
      },
      {
        field: "mode",
        headerName: translate("v1/discount_admin.fields.mode"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value }) => (
          <TableCell.Container align="center">
            {value === "online" ? <DevicesIcon /> : <StoreIcon />}
          </TableCell.Container>
        ),
      },
      {
        field: "company",
        headerName: translate("v1/discount_admin.fields.company"),
        minWidth: 250,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Link
            to={`/companies/edit/${value}`}
            value={companies?.find(company => company.id === value)?.name}
          />
        ),
      },
      {
        field: "name",
        headerName: translate("v1/discount_admin.fields.name"),
        minWidth: 300,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value}
            textAlign="left"
          />
        ),
      },
      {
        field: "type",
        headerName: translate("v1/discount_admin.fields.type"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value}
            textAlign="center"
          />
        ),
      },
      {
        field: "discount",
        headerName: translate("v1/discount_admin.fields.discount"),
        minWidth: 120,
        headerAlign: "right",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value ? `${value}%` : value}
            textAlign="right"
          />
        ),
      },
      {
        field: "original_price",
        headerName: translate("v1/discount_admin.fields.original_price"),
        minWidth: 120,
        headerAlign: "right",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value ? `R$ ${parseFloat(value).toFixed(2)}` : value}
            textAlign="right"
          />
        ),
      },
      {
        field: "gain_per_original_price",
        headerName: translate("v1/discount_admin.fields.gain_per_original_price"),
        minWidth: 120,
        headerAlign: "right",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value ? `R$ ${parseFloat(value).toFixed(2)}` : value}
            textAlign="right"
          />
        ),
      },
      {
        field: "usage",
        headerName: translate("v1/discount_admin.fields.usage"),
        minWidth: 120,
        headerAlign: "right",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value}
            textAlign="right"
          />
        ),
      },
      {
        field: "period",
        headerName: translate("v1/discount_admin.fields.start_date"),
        minWidth: 200,
        headerAlign: "center",
        sortable: false,
        renderCell: ({ row }) => {
          const { start_date, end_date } = row as any;

          if (!start_date && !end_date) {
            return (
              <TableCell.Text
                value="-"
                textAlign="center"
              />
            );
          }

          const format = (value?: string | null) =>
            value ? new Date(value).toLocaleDateString("pt-BR") : "—";

          const label = `${format(start_date)} - ${format(end_date)}`;

          let color: "default" | "success" | "error" = "default";
          if (end_date) {
            const end = new Date(end_date);
            const today = new Date();
            end.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            color = end < today ? "error" : "success";
          }

          return (
            <TableCell.Container align="center">
              <Chip
                label={label}
                color={color}
                variant="outlined"
                size="small"
              />
            </TableCell.Container>
          );
        },
      },
      {
        field: "total_transacted",
        headerName: translate("v1/discount_admin.fields.total_transacted"),
        minWidth: 120,
        headerAlign: "right",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value ? `R$ ${parseFloat(value).toFixed(2)}` : value}
            textAlign="right"
          />
        ),
      },
      {
        field: "featured",
        headerName: translate("v1/discount_admin.fields.featured"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value, row }) => (
          <TableCell.Container align="center">
            <FeaturedDiscountSwitch value={value} id={row.id} />
          </TableCell.Container>
        ),
      },
      {
        field: "active",
        headerName: translate("v1/discount_admin.fields.active"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value, row }) => (
          <TableCell.Container align="center">
            <ActiveDiscountSwitch value={value} id={row.id} />
          </TableCell.Container>
        ),
      },
      {
        field: "created_at",
        headerName: translate("v1/discount_admin.fields.created_at"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value }) => (
          <TableCell.Date value={value} />
        ),
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        headerAlign: "center",
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <TableCell.Container align="center">
            <EditButton hideText recordItemId={row.id} />
            <DeleteButton hideText recordItemId={row.id} onSuccess={onRefetch} />
          </TableCell.Container>
        ),
      },
    ],
    [translate, onRefetch, companies],
  );
};
